<template>
  <div ref="root" class="flex min-h-0 flex-1 flex-col gap-4">
    <!-- Always rendered, so screen readers hear the list load and its count change. -->
    <p class="sr-only" role="status">{{ listStatus }}</p>
    <h1 class="sr-only">PO Tracker</h1>
    <div class="flex flex-col gap-3">
      <SchedulingVendorTabs :model-value="currentLaneCode" :lanes="vendorLanes" hide-single @update:model-value="pickVendor">
        <template #actions>
          <PoTrackerActions
            :search="store.url.po"
            :planner="planner"
            :partner="partner"
            :transit-days="transitDays"
            @search="go({ po: $event })"
            @request-delivery="openRequest(null)"
            @mark-shipped="openMarkShipped(null)"
            @import-sheet="importDialog.show(null)"
          />
        </template>
      </SchedulingVendorTabs>
      <CustomerColorLegend />
    </div>

    <div v-if="directory === 'failed'" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
      {{ vendorStore.error }}
      <button type="button" class="ml-2 font-medium underline" @click="vendorStore.fetchVendors()">Retry</button>
    </div>
    <p v-else-if="directory === 'ready' && allowed.length === 0" class="card m-0 text-sm text-gray-500">No vendors are linked to your account. Ask a viaPhoton admin to add you.</p>
    <template v-else>
      <!-- The slot stays in the layout so a tab switch doesn't push the list down. -->
      <div class="h-0.5">
        <v-progress-linear v-if="showLoadingBar" indeterminate color="primary" height="2" aria-label="Loading POs" />
      </div>
      <div v-if="store.error" class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
        {{ store.error }}
        <button type="button" class="ml-2 font-medium underline" @click="store.loadList()">Retry</button>
      </div>

      <template v-if="focus === 'not_found'">
        <p class="card m-0 text-sm text-gray-600">
          No PO {{ store.url.po }} for {{ vendorName }}.
          <button type="button" class="font-semibold text-blue-700 hover:underline" @click="go({ po: '' })">Clear</button>
        </p>
      </template>

      <template v-else-if="store.list">
        <section
          v-if="boxMode === REQUESTS_BOX_MODE.ANSWER && openRequests.length > 0"
          class="flex flex-col gap-2 rounded-2xl border border-violet-200 bg-white p-2 shadow-sm"
          aria-labelledby="po-tracker-requests"
        >
          <h2 id="po-tracker-requests" class="m-0 px-1 pt-1 text-sm font-semibold text-violet-800">
            {{ requestsHeading(openRequests.length) }}
          </h2>
          <RequestLine
            v-for="request in openRequests"
            :key="request.id"
            :po-number="request.poNumber"
            :ship-by="request.shipBy"
            :to-plan="request.toPlan"
            :note="request.note"
            :requested-at="request.requestedAt"
            :requested-by="request.requestedBy"
            can-answer
            :answering="store.answering === request.poNumber"
            @open-po="openPo(request.poNumber)"
            @answer="answer(request.poNumber)"
          />
        </section>
        <section
          v-else-if="boxMode === REQUESTS_BOX_MODE.REVIEW && focus !== 'single' && openRequests.length > 0"
          class="flex flex-col gap-2 rounded-2xl border border-violet-200 bg-white px-3 py-2 shadow-sm"
          aria-labelledby="po-tracker-requests-review"
        >
          <div class="flex flex-wrap items-center gap-x-2">
            <h2 id="po-tracker-requests-review" class="m-0 text-sm font-semibold text-violet-800">{{ openRequestsSummary(openRequests.length) }}</h2>
            <button
              type="button"
              class="group inline-flex items-center gap-0.5 text-sm font-semibold text-blue-700"
              :aria-expanded="reviewOpen"
              aria-controls="po-tracker-requests-review-list"
              @click="toggleReview"
            >
              <!-- Underlines the word only: on the button it would run under the chevron too, and flip above it when rotated. -->
              <span class="group-hover:underline">Review</span>
              <v-icon size="18" icon="mdi-chevron-down" class="transition-transform duration-150 ease-out motion-reduce:transition-none" :class="reviewOpen ? 'rotate-180' : ''" />
            </button>
          </div>
          <div v-show="reviewOpen" id="po-tracker-requests-review-list" class="flex flex-col gap-1">
            <RequestLine
              v-for="request in openRequests"
              :key="request.id"
              :po-number="request.poNumber"
              :ship-by="request.shipBy"
              :to-plan="request.toPlan"
              :note="request.note"
              :requested-at="request.requestedAt"
              :requested-by="request.requestedBy"
              @open-po="go({ po: request.poNumber })"
            />
          </div>
        </section>
        <UpdatesBox
          v-if="planner && focus !== 'single' && store.updates && store.updates.events.length > 0"
          :updates="store.updates"
          :vendor-name="vendorName"
          @open-po="go({ po: $event })"
          @show-all="go({ tab: PO_TRACKER_TAB.UPDATED, po: '' })"
        />
        <p v-if="focus === 'single'" class="m-0 text-sm text-gray-600">
          Showing one PO ·
          <button type="button" class="font-semibold text-blue-700 hover:underline" @click="go({ po: '' })">Show all POs</button>
        </p>
        <PoTrackerTabs v-else :tab="store.url.tab" :counts="store.list.tabCounts" :show-updated="planner" @select="go({ tab: $event })" />

        <PoTrackerTable
          :rows="store.list.rows"
          :colors="colors"
          :expanded="store.expanded"
          :show-new="planner"
          :empty-text="emptyText"
          :can-show-all="store.url.tab !== PO_TRACKER_TAB.ALL || store.url.po !== ''"
          @toggle="store.toggle($event)"
          @show-all="go({ tab: PO_TRACKER_TAB.ALL, po: '' })"
          @mark-shipped="openMarkShipped($event)"
          @shipping-details="shippingDialog.show($event)"
          @request-delivery="openRequest($event)"
        />

        <PoTrackerPager
          v-if="focus !== 'single' && store.list.total > store.list.pageSize"
          :page="store.list.page"
          :page-size="store.list.pageSize"
          :total="store.list.total"
          @page="go({ page: $event })"
        />

        <p v-if="store.list.syncError" class="m-0 text-xs font-semibold text-amber-800">
          Couldn't refresh — showing data from {{ store.list.syncedAt ? formatUpdatedAt(store.list.syncedAt) : '—' }} ·
          <button type="button" class="font-semibold text-blue-700 hover:underline" @click="store.loadList()">Refresh</button>
        </p>
      </template>
    </template>

    <!-- Each dialog stays mounted after close so v-dialog can play its exit transition. -->
    <MarkShippedDialog
      v-if="shipDialog.payload.value && store.url.vendorId"
      :key="shipDialog.key.value"
      :open="shipDialog.open.value"
      :vendor-id="store.url.vendorId"
      :initial="shipDialog.payload.value.initial"
      @close="shipDialog.close()"
    />
    <!-- The payload is null, so the key (bumped by show) says it was opened. -->
    <ImportSheetDialog
      v-if="importDialog.key.value > 0 && store.url.vendorId"
      :key="importDialog.key.value"
      :open="importDialog.open.value"
      :vendor-id="store.url.vendorId"
      @close="importDialog.close()"
      @done="store.afterWrite($event)"
    />
    <RequestDeliveryDialog
      v-if="requestDialog.payload.value && store.url.vendorId && transitDays !== null"
      :key="requestDialog.key.value"
      :open="requestDialog.open.value"
      :rows="store.list?.rows ?? []"
      :preset="requestDialog.payload.value.preset"
      :vendor-id="store.url.vendorId"
      :vendor-name="vendorName"
      :transit-days="transitDays"
      @close="requestDialog.close()"
    />
    <ShippingDetailsDialog
      v-if="shippingDialog.payload.value && store.url.vendorId"
      :key="shippingDialog.key.value"
      :open="shippingDialog.open.value"
      :event="shippingDialog.payload.value"
      :vendor-id="store.url.vendorId"
      @close="shippingDialog.close()"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import CustomerColorLegend from '@/components/capacity/CustomerColorLegend.vue';
  import ImportSheetDialog from '@/components/poTracker/ImportSheetDialog.vue';
  import MarkShippedDialog from '@/components/poTracker/MarkShippedDialog.vue';
  import PoTrackerActions from '@/components/poTracker/PoTrackerActions.vue';
  import PoTrackerPager from '@/components/poTracker/PoTrackerPager.vue';
  import PoTrackerTable from '@/components/poTracker/PoTrackerTable.vue';
  import PoTrackerTabs from '@/components/poTracker/PoTrackerTabs.vue';
  import RequestDeliveryDialog from '@/components/poTracker/RequestDeliveryDialog.vue';
  import RequestLine from '@/components/poTracker/RequestLine.vue';
  import ShippingDetailsDialog from '@/components/poTracker/ShippingDetailsDialog.vue';
  import UpdatesBox from '@/components/poTracker/UpdatesBox.vue';
  import SchedulingVendorTabs from '@/components/scheduling/SchedulingVendorTabs.vue';
  import { useAccess } from '@/composables/useAccess';
  import { useKeptDialog } from '@/composables/useKeptDialog';
  import { useDelayedVisible } from '@/composables/useDelayedVisible';
  import { useAccessStore } from '@/store/accessStore';
  import { useCalendarPrivacyStore } from '@/store/calendarPrivacyStore';
  import { useCustomerMappingStore } from '@/store/customerMappingStore';
  import { usePoTrackerStore } from '@/store/poTrackerStore';
  import { useVendorStore } from '@/store/vendorStore';
  import { PERMISSIONS } from '@/types/access';
  import { PO_TRACKER_TAB, type PoDetail, type Shipment, type ShippingEvent } from '@/types/poTracker';
  import { resolvePoSegmentColor } from '@/utils/customerColors';
  import { formatUpdatedAt } from '@/utils/dates';
  import {
    answerTarget,
    emptyListText,
    listStatusText,
    openRequestsSummary,
    readRequestsReviewOpen,
    REQUESTS_BOX_MODE,
    requestsBoxMode,
    requestsHeading,
    requestsReviewKey,
    writeRequestsReviewOpen,
  } from '@/utils/poTracker';
  import { focusState, parseTrackerQuery, toTrackerQuery, type TrackerUrlState, vendorDecision, vendorDirectoryState } from '@/utils/poTrackerQuery';
  import { vendorDisplayName } from '@/utils/vendorArchive';

  const FIRST_PAGE = 1;
  const LOADING_BAR_DELAY_MS = 200;
  const LOADING_BAR_HOLD_MS = 500;

  const route = useRoute();
  const router = useRouter();
  const store = usePoTrackerStore();
  const showLoadingBar = useDelayedVisible(
    computed(() => store.loading),
    { delayMs: LOADING_BAR_DELAY_MS, holdMs: LOADING_BAR_HOLD_MS }
  );
  const vendorStore = useVendorStore();
  const mappingStore = useCustomerMappingStore();
  const privacyStore = useCalendarPrivacyStore();
  const { hasPermission, hasAllVendors, scopedVendors } = useAccess();

  const planner = computed(() => hasPermission(PERMISSIONS.REQUEST_PO_DELIVERY));
  const partner = computed(() => hasPermission(PERMISSIONS.WRITE_PO_SHIPMENTS));
  const boxMode = computed(() => requestsBoxMode(hasPermission));

  const canReadVendors = hasPermission(PERMISSIONS.READ_VENDORS);
  // A scoped caller's vendors come with /me; only the full directory has to be fetched.
  const vendorsReady = ref(!hasAllVendors.value);

  const allowed = computed(() => (hasAllVendors.value ? vendorStore.activeVendors : scopedVendors.value));
  // The shared vendor row keys vendors by code: the display name, with the full name as its title.
  const vendorLanes = computed(() => allowed.value.map((vendor) => ({ id: vendor.id, vendorCode: vendorDisplayName(vendor), vendorName: vendor.name })));
  const currentLaneCode = computed(() => vendorLanes.value.find((lane) => lane.id === store.url.vendorId)?.vendorCode ?? null);

  function pickVendor(code: string): void {
    const lane = vendorLanes.value.find((l) => l.vendorCode === code);
    if (lane && lane.id !== store.url.vendorId) go({ vendorId: lane.id, po: '' });
  }
  const currentVendor = computed(() => allowed.value.find((vendor) => vendor.id === store.url.vendorId) ?? null);
  const vendorName = computed(() => (currentVendor.value ? vendorDisplayName(currentVendor.value) : ''));
  // Carried by the list, so it needs no read:vendors.
  const transitDays = computed(() => store.list?.transitDays ?? null);

  const focus = computed(() => focusState(store.url.po, store.list, store.listPo));
  const listStatus = computed(() => listStatusText({ loading: store.loading, total: store.list?.total ?? null, stale: Boolean(store.list?.syncError) }));

  // Only the full vendor directory is fetched; a scoped caller's vendors came with /me.
  const directory = computed(() =>
    vendorDirectoryState({
      ready: vendorsReady.value,
      loading: hasAllVendors.value && vendorStore.loading,
      error: hasAllVendors.value ? vendorStore.error : null,
    })
  );

  // Indexed by row so the partner palette stays stable within a page.
  const colors = computed(
    () =>
      new Map(
        (store.list?.rows ?? []).map((row, index) => [
          row.poNumber,
          resolvePoSegmentColor(row.poNumber, mappingStore.items, { hideCustomerMappings: privacyStore.hideCustomerMappings, index }),
        ])
      )
  );

  const emptyText = computed(() => emptyListText({ tab: store.url.tab, po: store.url.po, vendorName: vendorName.value }));

  const requestDialog = useKeptDialog<{ preset: string | null }>();

  // Vendor-wide and already ordered by the backend, so the count doesn't change with the tab or page.
  const openRequests = computed(() => store.list?.openRequests ?? []);

  function openRequest(preset: string | null): void {
    // Until the list loads there is no transit time, so no ship-by to show or send.
    if (transitDays.value === null) return;
    requestDialog.show({ preset });
  }

  const root = ref<HTMLElement | null>(null);
  // A PO answered from the box that isn't on this page: its form opens once the single-PO view shows it.
  const pendingAnswer = ref<string | null>(null);

  function openPo(poNumber: string): void {
    if (answerTarget(poNumber, store.list?.rows ?? []) === 'single') {
      go({ po: poNumber });
      return;
    }
    if (!store.expanded.has(poNumber)) store.toggle(poNumber);
    void nextTick(() => root.value?.querySelector(`[data-po-row="${CSS.escape(poNumber)}"]`)?.scrollIntoView({ block: 'nearest' }));
  }

  function answer(poNumber: string): void {
    if (answerTarget(poNumber, store.list?.rows ?? []) === 'single') {
      pendingAnswer.value = poNumber;
      go({ po: poNumber });
      return;
    }
    store.startAnswer(poNumber);
    openPo(poNumber);
  }

  // A planner's review of open delivery requests: collapsed unless this user left it open.
  const accessStore = useAccessStore();
  const reviewKey = computed(() => requestsReviewKey(accessStore.me?.email ?? ''));
  const storage = (): Storage | null => {
    try {
      return window.localStorage;
    } catch {
      return null;
    }
  };
  const reviewOpen = ref(readRequestsReviewOpen(storage(), reviewKey.value));
  watch(reviewKey, (key) => {
    reviewOpen.value = readRequestsReviewOpen(storage(), key);
  });

  function toggleReview(): void {
    reviewOpen.value = !reviewOpen.value;
    writeRequestsReviewOpen(storage(), reviewKey.value, reviewOpen.value);
  }

  // The header opens Mark shipped empty; a row opens it with its PO loaded and that shipment ticked.
  const shipDialog = useKeptDialog<{ initial: { detail: PoDetail; shipmentId: string } | null }>();
  const shippingDialog = useKeptDialog<ShippingEvent>();
  const importDialog = useKeptDialog<null>();

  function openMarkShipped(shipment: Shipment | null): void {
    const detail = shipment ? store.cachedDetail(shipment.poNumber) : undefined;
    shipDialog.show({ initial: shipment && detail ? { detail, shipmentId: shipment.id } : null });
  }

  // Every UI change goes through the URL; the route watcher is the only thing that loads.
  function go(patch: Partial<TrackerUrlState>): void {
    void router.replace({ query: toTrackerQuery({ ...store.url, page: FIRST_PAGE, ...patch }) });
  }

  const sameState = (a: TrackerUrlState, b: TrackerUrlState): boolean => a.vendorId === b.vendorId && a.tab === b.tab && a.po === b.po && a.page === b.page;

  let synced = false;

  function syncFromRoute(): void {
    // A failed or running directory fetch decides nothing: no 403 and no "No vendors" until it succeeds.
    if (directory.value !== 'ready') return;
    const parsed = parseTrackerQuery(route.query, planner.value);
    const decision = vendorDecision(parsed.vendorId, allowed.value);
    // Nothing is ever requested for an out-of-scope vendor.
    if (decision.kind === 'forbidden') {
      void router.replace({ name: 'Forbidden' });
      return;
    }
    if (decision.kind === 'none') return;
    const next = { ...parsed, vendorId: decision.id };
    if (parsed.vendorId === null) {
      void router.replace({ query: toTrackerQuery(next) });
      return;
    }
    // A vendor directory refresh re-runs this with the same URL; don't reload for it.
    if (synced && sameState(next, store.url)) return;
    synced = true;
    store.setUrl(next);
    void store.loadList();
    void store.loadUpdates();
  }

  watch([() => route.query, allowed, directory], syncFromRoute, { immediate: true });

  // A shared link opens its PO expanded, with the answer form open when the box sent it here.
  watch([focus, () => store.url.po], ([state, po]) => {
    if (state === 'not_found') pendingAnswer.value = null;
    if (state !== 'single') return;
    if (!store.expanded.has(po)) store.toggle(po);
    if (pendingAnswer.value === po) {
      pendingAnswer.value = null;
      store.startAnswer(po);
    }
  });

  // The store's queries run only while this page is open.
  onMounted(() => {
    store.pageOpen = true;
  });
  onUnmounted(() => {
    store.pageOpen = false;
  });

  onMounted(async () => {
    if (!mappingStore.loaded) void mappingStore.load();
    if (canReadVendors) {
      await vendorStore.fetchVendors();
    }
    vendorsReady.value = true;
  });
</script>
